import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { signInWithPopup, signOut, onAuthStateChanged, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth, provider, microsoftProvider } from '../../firebase';
import axios from 'axios';

// Store the unsubscribe function outside of Redux state
let authUnsubscribeFunction = null;

// Async thunk for Google login
export const loginWithGoogle = createAsyncThunk(
  'user/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const result = await signInWithPopup(auth, provider);

      const userData = {
        uid: result.user.uid,
        name: result.user.displayName,
        email: result.user.email,
        photoURL: result.user.photoURL,
        provider: 'google',
        isAuthenticated: true
      };

      return userData;
    } catch (error) {
      console.error('Google login failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for Microsoft login
export const loginWithMicrosoft = createAsyncThunk(
  'user/loginWithMicrosoft',
  async (_, { rejectWithValue }) => {
    try {
      const result = await signInWithPopup(auth, microsoftProvider);

      const userData = {
        uid: result.user.uid,
        name: result.user.displayName,
        email: result.user.email,
        photoURL: result.user.photoURL,
        provider: 'microsoft',
        isAuthenticated: true
      };

      return userData;
    } catch (error) {
      console.error('Microsoft login failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for sending email sign-in link
export const sendEmailSignInLink = createAsyncThunk(
  'user/sendEmailSignInLink',
  async (email, { rejectWithValue }) => {
    try {
      // First check if the email is allowed
      const response = await axios.get(`https://check-access-jp2nkft6ja-uk.a.run.app?email=${encodeURIComponent(email)}`);

      if (response.data !== 'allow') {
        return rejectWithValue('Access denied. You are not authorized to access this application.');
      }

      // Configure the email link action
      const actionCodeSettings = {
        url: window.location.origin + '/email-signin',
        handleCodeInApp: true,
      };

      // Send the email link
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      // Store the email locally to complete sign-in later
      window.localStorage.setItem('emailForSignIn', email);

      return { email };
    } catch (error) {
      console.error('Failed to send email link:', error);
      if (error.response?.data) {
        return rejectWithValue(`Access check failed: ${error.response.data}`);
      }
      return rejectWithValue(error.message || 'Failed to send sign-in link');
    }
  }
);

// Async thunk for completing email link sign-in
export const completeEmailSignIn = createAsyncThunk(
  'user/completeEmailSignIn',
  async (_, { rejectWithValue }) => {
    try {
      // Check if the current URL is a sign-in link
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        return rejectWithValue('Invalid sign-in link');
      }

      // Get the email from localStorage
      let email = window.localStorage.getItem('emailForSignIn');

      if (!email) {
        // If email is not in localStorage, prompt the user
        email = window.prompt('Please provide your email for confirmation');
      }

      if (!email) {
        return rejectWithValue('Email is required to complete sign-in');
      }

      // Complete the sign-in
      const result = await signInWithEmailLink(auth, email, window.location.href);

      const userData = {
        uid: result.user.uid,
        name: result.user.displayName || email.split('@')[0],
        email: result.user.email,
        photoURL: result.user.photoURL,
        provider: 'email',
        isAuthenticated: true
      };

      // Clear the email from localStorage
      window.localStorage.removeItem('emailForSignIn');

      return userData;
    } catch (error) {
      console.error('Email sign-in failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      return null;
    } catch (error) {
      console.error('Logout failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for setting up auth state listener
export const setupAuthListener = createAsyncThunk(
  'user/setupAuthListener',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Clean up existing listener if it exists
      if (authUnsubscribeFunction) {
        authUnsubscribeFunction();
      }

      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const userData = {
                uid: user.uid,
                name: user.displayName,
                email: user.email,
                photoURL: user.photoURL,
                provider: user.providerData[0]?.providerId || 'unknown',
                isAuthenticated: true
              };

              // Dispatch the auth state change
              dispatch(setAuthUser(userData));
            } catch (error) {
              console.error('Error getting user token:', error);
              dispatch(setAuthUser(null));
            }
          } else {
            // User is signed out
            dispatch(setAuthUser(null));
          }

          // Mark as initialized after first auth check
          dispatch(setAuthInitialized(true));
        });

        // Store the unsubscribe function outside of Redux state
        authUnsubscribeFunction = unsubscribe;

        // Return a success indicator instead of the function
        resolve({ success: true });
      });
    } catch (error) {
      console.error('Auth listener setup failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Function to clean up auth listener (call this when your app unmounts)
export const cleanupAuthListener = () => {
  if (authUnsubscribeFunction) {
    authUnsubscribeFunction();
    authUnsubscribeFunction = null;
  }
};

// Async thunk for initializing user state
export const initializeUser = createAsyncThunk(
  'user/initialize',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Setup the auth listener
      await dispatch(setupAuthListener());
      return true;
    } catch (error) {
      console.error('User initialization failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
      state.error = null;
    },
    setAuthUser(state, action) {
      state.user = action.payload;
      state.error = null;
    },
    setAuthInitialized(state, action) {
      state.isInitialized = action.payload;
    },
    updateUser(state, action) {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    clearUser(state) {
      state.user = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    setTokenExpired(state) {
      state.user = null;
      state.error = 'Session expired. Please login again.';
    },
    updateUserToken(state, action) {
      if (state.user) {
        state.user.token = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Google Login
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Microsoft Login
      .addCase(loginWithMicrosoft.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithMicrosoft.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginWithMicrosoft.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Send Email Sign-In Link
      .addCase(sendEmailSignInLink.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendEmailSignInLink.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendEmailSignInLink.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Complete Email Sign-In
      .addCase(completeEmailSignIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeEmailSignIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(completeEmailSignIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.error = null;
        state.isLoading = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Initialize User
      .addCase(initializeUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeUser.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(initializeUser.rejected, (state) => {
        state.isInitialized = true;
        state.isLoading = false;
        state.user = null;
      })
      // Modified: setupAuthListener now returns a success indicator
      .addCase(setupAuthListener.fulfilled, (state, action) => {
        // No need to store the unsubscribe function in state
        // It's handled outside of Redux
      });
  }
});

export const {
  setUser,
  setAuthUser,
  setAuthInitialized,
  updateUser,
  clearUser,
  clearError,
  setTokenExpired,
  updateUserToken
} = userSlice.actions;

export default userSlice.reducer;